// utils/getColumnSearchAndSortProps.js
import React from 'react';
import { Input, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

/**
 * Get value from object using flat or nested key path
 * @param {object} obj - The object (e.g., a record)
 * @param {string|string[]} path - The key or nested path (e.g., 'name' or ['user', 'email'])
 */
const getValue = (obj, path) => {
  if (!obj || !path) return '';
  if (Array.isArray(path)) {
    return path.reduce((acc, key) => (acc ? acc[key] : ''), obj);
  }
  return obj[path];
};

/**
 * Generates column search & sort props.
 * @param {string|string[]} dataIndex - Flat or nested key.
 * @param {string} placeholder - Optional input placeholder.
 * @param {'string'|'date'|'number'} sortType - Optional type for sorting.
 */
export const GetColumnSearchAndSortProps = (dataIndex, placeholder, sortType = null) => {
  const sorter = sortType
    ? (a, b) => {
        const aVal = getValue(a, dataIndex);
        const bVal = getValue(b, dataIndex);
        if (sortType === 'date') return new Date(aVal) - new Date(bVal);
        if (sortType === 'number') return aVal - bVal;
        if (sortType === 'string') return (aVal || '').toString().localeCompare((bVal || '').toString());
        return 0;
      }
    : undefined;

  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <div style={{ padding: 8 }}>
        {sortType === 'date' ? (
          <DatePicker
            style={{ width: 188 }}
            placeholder={placeholder || 'Select date'}
            value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
            onChange={(date, dateString) => {
              if (dateString) {
                setSelectedKeys([dateString]);
              } else {
                setSelectedKeys([]);
              }
              confirm({ closeDropdown: false });
            }}
            format="YYYY-MM-DD"
          />
        ) : (
          <Input
            placeholder={placeholder || 'Search'}
            value={selectedKeys[0]}
            onChange={e => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              confirm({ closeDropdown: false });
            }}
            style={{ width: 188 }}
            autoFocus
          />
        )}
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const fieldValue = getValue(record, dataIndex);
      if (!fieldValue) return false;
      if (sortType === 'date') {
        const recordDate = dayjs(fieldValue).format('YYYY-MM-DD');
        return recordDate === value;
      }
      return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
    },
    sorter,
  };
};
